import React, { useCallback, useEffect, useState } from 'react';
import { downloadTriageLetter, fetchTriageLetterUrl } from '../../utils/triageLetterDownload';
import HarbourFlyout from '../../components/harbour/HarbourFlyout';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import { letterSharedWithRole } from '../../utils/harbourPupils';
import { PATHWAY_LABELS } from '../../utils/triagePathways';
import { FAMILY_SERVICE_NAME, letterEmbedUrl } from '../../utils/familyPortal';
import LetterLeaflets from '../family/LetterLeaflets';
import '../../components/harbour/harbour.css';
import '../family/familyReferral.css';

/*
 * SENCO portal · outcome letter viewer. Opens over Your pupils at
 * /rms/pupils/:patientRecordId/letter/:pathwayKey, from the pupil record's
 * "View outcome letter". The same letter, states and wording as the parent
 * or carer's letter view, on the professional surface (plum Download).
 *
 * The letter is fetched through the guarded signed-URL callable
 * (rmsFetchTriageDocument), which serves it only to a linked school user once
 * a clinician has sent the letter to the school role; the record shows the
 * button under the same rule, so this view normally opens straight onto it.
 */
export default function HarbourOutcomeLetterFlyout({ pupil, pathwayKey, orgId, onClose }) {
  const [letterState, setLetterState] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [letterUrl, setLetterUrl] = useState(null);
  const [fetchAttempt, setFetchAttempt] = useState(0);
  const [downloading, setDownloading] = useState(false);

  const patient = pupil?.patient || {};
  const pathwayLabel = PATHWAY_LABELS[pathwayKey] || '';
  const letterAvailable = Boolean(pathwayLabel) && letterSharedWithRole(patient, pathwayKey, 'school_user');

  const fetchSignedUrl = useCallback(
    (disposition) => fetchTriageLetterUrl({
      orgId,
      patientRecordId: pupil.id,
      pathway: pathwayKey,
      disposition,
    }),
    [orgId, pupil.id, pathwayKey]
  );

  useEffect(() => {
    if (!letterAvailable) return undefined;
    let cancelled = false;
    (async () => {
      setLetterState('loading');
      try {
        const res = await fetchSignedUrl('inline');
        const url = res?.data?.downloadUrl;
        if (!cancelled) {
          if (url) {
            setLetterUrl(url);
            setLetterState('ready');
          } else {
            setLetterState('error');
          }
        }
      } catch (_err) {
        if (!cancelled) setLetterState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [letterAvailable, fetchSignedUrl, fetchAttempt]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadTriageLetter({ orgId, patientRecordId: pupil.id, pathway: pathwayKey });
    } catch (err) {
      console.error('Failed to download the outcome letter:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <HarbourFlyout
      width="wide"
      eyebrow={{ tone: 'referral', label: 'Referral pathway' }}
      title={pupil.name}
      year={pupil.yearGroup}
      onClose={onClose}
      closeLabel="Close the letter"
    >
      <div className="family-letter-flyout-body">
        <div className="family-letter-column">
          <div className="family-letter-card">
            <div className="family-letter-card__head">
              <h2 className="harbour-heading-24 family-letter-card__title">
                {`Outcome letter: ${pathwayLabel} referral`}
              </h2>
              <p className="harbour-body-16 family-letter-card__meta">
                {`From: ${pathwayLabel} Service, ${FAMILY_SERVICE_NAME}`}
              </p>
            </div>
            {!letterAvailable ? (
              <HarbourNotice kind="info" title="The outcome letter is not available yet." />
            ) : (
              <>
                {letterState === 'loading' && (
                  <div className="family-letter-loading animate-pulse" aria-hidden="true">
                    <div className="harbour-skeleton" />
                    <div className="harbour-skeleton" />
                    <div className="harbour-skeleton" />
                  </div>
                )}
                {letterState === 'error' && (
                  <div className="family-page-error">
                    <HarbourNotice kind="error" title="The letter could not be loaded" />
                    <HarbourButton kind="secondary" onClick={() => setFetchAttempt((n) => n + 1)}>
                      Try again
                    </HarbourButton>
                  </div>
                )}
                {letterState === 'ready' && (
                  <>
                    <iframe title="Outcome letter" src={letterEmbedUrl(letterUrl)} className="family-letter-frame" />
                    <LetterLeaflets patient={patient} pathwayKey={pathwayKey} />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {/* Only once the letter is actually on the page: a Download for a letter
          that failed to load is an offer the page cannot keep. */}
      {letterAvailable && letterState === 'ready' && (
        <div className="harbour-submit-bar">
          <HarbourButton
            kind="primary"
            leadingIcon="download"
            busy={downloading}
            aria-busy={downloading}
            disabled={downloading}
            onClick={handleDownload}
          >
            Download
          </HarbourButton>
        </div>
      )}
    </HarbourFlyout>
  );
}
